import { useQuery } from "@tanstack/react-query";
import type { MessageKey } from "../i18n/en";
import { useI18n } from "../i18n/I18nProvider";
import { paths } from "../lib/endpoints";
import { ErrorState } from "../components/ui/ErrorState";
import { devLoginAvailableQuery } from "./queries";
import { AuthShell } from "./AuthShell";
import { DevLoginForm } from "./DevLoginForm";
import { GoogleSignInLink } from "./GoogleSignInLink";

export interface LoginPageProps {
  /** Invitation token from /invite/:token or ?invite=, carried through sign-in. */
  invite?: string | undefined;
  /** Stable error code the API put in ?error= after a failed Google sign-in. */
  error?: string | undefined;
}

const LOGIN_ERROR_KEYS: Record<string, MessageKey> = {
  oauth_state_invalid: "auth.error.oauth_state_invalid",
  email_unverified: "auth.error.email_unverified",
  not_invited: "auth.error.not_invited",
  invite_invalid: "auth.error.invite_invalid",
  invite_email_mismatch: "auth.error.invite_email_mismatch",
  oauth_not_configured: "auth.error.oauth_not_configured",
  oauth_exchange_failed: "auth.error.oauth_exchange_failed",
  rate_limited: "auth.error.rate_limited",
};

/** Unknown codes fall back to the generic message, never to the raw code. */
function loginErrorKey(code: string | undefined): MessageKey | null {
  if (!code) return null;
  return LOGIN_ERROR_KEYS[code] ?? "auth.error.unknown";
}

/**
 * Sign-in page. The dev form renders only when the API reports dev login (see devLoginAvailableQuery);
 * the Google button is a plain link that the API turns into the OAuth redirect.
 */
export function LoginPage({ invite, error }: LoginPageProps) {
  const { t } = useI18n();
  const errorKey = loginErrorKey(error);
  const devLogin = useQuery(devLoginAvailableQuery);
  const googleHref = invite
    ? `${paths.googleStart}?invite=${encodeURIComponent(invite)}`
    : paths.googleStart;

  return (
    <AuthShell>
      <header className="space-y-1 text-center">
        <h1 className="font-display text-3xl leading-none font-extrabold tracking-[0.01em] uppercase">
          {invite ? t("auth.invite.title") : t("auth.login.title")}
        </h1>
        <p className="text-operate text-ink-2">
          {invite ? t("auth.invite.text") : t("auth.login.subtitle")}
        </p>
      </header>
      {errorKey ? <ErrorState>{t(errorKey)}</ErrorState> : null}
      {invite ? (
        <p className="rounded-control bg-ice-2 p-3 text-note text-ink-2">
          {t("auth.login.inviteNote")}
        </p>
      ) : null}
      <GoogleSignInLink href={googleHref} />
      {devLogin.data ? <DevLoginForm invite={invite} /> : null}
    </AuthShell>
  );
}
